import Cookies from 'js-cookie';

type CookieOptions = {
  otGroup?: string;
  expires?: number;
};

const consentCookieCallbacks = new Map<string, () => void>();
let consentListenersBound = false;

const getConsentForGroup = (otGroup: string): boolean | undefined => {
  // Type assertion needed: cohesion.consent() exists at runtime but isn't in the CohesionFn type definition
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const consentGroups = (window?.cohesion as any)?.consent?.('list');

  if (!Array.isArray(consentGroups)) {
    return undefined;
  }

  return consentGroups.includes(otGroup);
};

const runConsentCookieCallbacks = (): void => {
  consentCookieCallbacks.forEach((callback) => callback());
};

const ensureConsentListeners = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (consentListenersBound) {
    return true;
  }

  if (typeof window.cohesion !== 'function') {
    return false;
  }

  window.cohesion('consent:ready', runConsentCookieCallbacks);
  window.cohesion('consent:changed', runConsentCookieCallbacks);
  consentListenersBound = true;

  return true;
};

export const getCookie = (name: string): string | null => {
  try {
    const value = Cookies.get(name);
    return value || null;
  } catch (error) {
    console.error(`Error getting cookie ${name}:`, error);
    return null;
  }
};

export const deleteCookie = (name: string): void => {
  const domain = new URL(window.location.href);
  const { hostname } = domain;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const domainAttr = isLocalhost ? '' : 'domain=.frontier.com;';

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${domainAttr}`;
};

export const setCookie = (name: string, value: unknown, options?: CookieOptions): void => {
  if (typeof window === 'undefined') return;

  if (!options?.otGroup) {
    Cookies.set(name, String(value), options);
    return;
  }

  const { otGroup, ...cookieOptions } = options;
  const callbackKey = `${name}:${otGroup}`;

  const applyCookieWithConsent = () => {
    const hasConsent = getConsentForGroup(otGroup);

    if (hasConsent === undefined) {
      return;
    }

    if (!hasConsent) {
      deleteCookie(name);
      return;
    }

    Cookies.set(name, String(value), cookieOptions);
  };

  consentCookieCallbacks.set(callbackKey, applyCookieWithConsent);
  applyCookieWithConsent();

  if (!ensureConsentListeners()) {
    Cookies.set(name, String(value), cookieOptions);
  }
};
